import { useEffect, useState } from 'react'
import { buildPlan, nextStep, type ArriveBy, type PlanStep } from '../../shared/ferryPlan'
import { clock, countdown, destinationOf, originOf, type Trip } from '../lib/ferry'
import { t } from '../lib/i18n'
import Icon, { type IconName } from './Icon'

const ICON: Record<PlanStep['kind'], IconName> = {
  leave: 'home', travel: 'route', park: 'car', arrive: 'ship',
  counter: 'ticket', vehicleCutoff: 'car', checkIn: 'clock',
  boarding: 'people', depart: 'ferry', arriveDest: 'check',
}

/** Remembered per browser: how far this person is from the terminal. */
const TRAVEL_KEY = 'ferry.travelMinutes'
export function savedTravelMinutes(): number | null {
  try {
    const v = Number(localStorage.getItem(TRAVEL_KEY))
    return Number.isFinite(v) && v > 0 ? v : null
  } catch { return null }
}

/**
 * The plan for one trip.
 *
 * ── Ported from travel-day, and why ──────────────────────────────────────────
 *
 * Travel Day exists because the useful answer to "my flight is at 3" is not the
 * flight time — it is when to walk out the front door. A ferry is the same
 * problem with sharper edges: the Ceiba terminal is forty minutes from San
 * Juan, the car lane closes an hour before the boat, and the lot fills on a
 * Friday. Somebody who reads "departs 15:30" and leaves at 14:45 has already
 * missed it, and the next one may be tomorrow.
 *
 * The arithmetic is in `shared/ferryPlan.ts` — shared with the server, so the
 * cutoff that refuses a late booking and the cutoff drawn on this timeline are
 * the same number by construction.
 */
export default function TripPlan({ trip }: { trip: Trip }) {
  const [travelMinutes, setTravelMinutes] = useState<number | null>(savedTravelMinutes())
  const [now, setNow] = useState(Date.now())

  // A plan is a clock, so it ticks.
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 30_000)
    return () => window.clearInterval(timer)
  }, [])

  const [arriveBy, setArriveBy] = useState<ArriveBy>(() => {
    try { return (localStorage.getItem('ferry.arriveBy') as ArriveBy) || 'drive' } catch { return 'drive' }
  })

  const plan = buildPlan({
    scheduledDeparture: trip.scheduled_departure,
    scheduledArrival: trip.scheduled_arrival,
    actualDeparture: trip.actual_departure,
    route: trip.route,
    vesselName: trip.vessel_name,
    travelMinutes,
    arriveBy,
    bringingVehicle: trip.hasVehicle,
    hasTicket: trip.status !== 'held',
    status: trip.sailing_status,
  }, now)

  const next = nextStep(plan, now)

  const setTravel = (v: number | null) => {
    setTravelMinutes(v)
    try {
      if (v) localStorage.setItem(TRAVEL_KEY, String(v))
      else localStorage.removeItem(TRAVEL_KEY)
    } catch { /* private mode; the preference simply does not persist */ }
  }
  const setArrive = (v: ArriveBy) => {
    setArriveBy(v)
    try { localStorage.setItem('ferry.arriveBy', v) } catch { /* private mode */ }
  }

  return (
    <div className="plan">
      {/*
        * The headline number, and it is deliberately the only large one. Somebody
        * opening this on a Friday morning wants one thing: when do I leave.
        */}
      {plan.leaveHomeAt != null ? (
        <div className={`leave-now${now >= plan.leaveHomeAt ? ' due' : ''}`}>
          <Icon name="home" size={22} />
          <div>
            <span className="muted small">{now >= plan.leaveHomeAt ? t('plan.shouldHaveLeft') : t('plan.leaveAt')}</span>
            <strong>{clock(plan.leaveHomeAt)}</strong>
            <em>{countdown(Math.round((plan.leaveHomeAt - now) / 60000))}</em>
          </div>
        </div>
      ) : (
        <p className="notice"><Icon name="info" size={16} />{t('plan.tellUsHowFar')}</p>
      )}

      <div className="plan-inputs">
        <label>
          <span className="muted small">{t('plan.minutesAway')}</span>
          <input
            type="number" min={0} max={240} inputMode="numeric"
            value={travelMinutes ?? ''}
            placeholder="40"
            onChange={(e) => setTravel(e.target.value ? Math.max(0, Number(e.target.value)) : null)}
          />
        </label>
        <label>
          <span className="muted small">{t('plan.gettingThere')}</span>
          <select value={arriveBy} onChange={(e) => setArrive(e.target.value as ArriveBy)}>
            <option value="drive">{t('plan.driving')}</option>
            <option value="dropoff">{t('plan.droppedOff')}</option>
            <option value="taxi">{t('plan.taxi')}</option>
            <option value="walk">{t('plan.walking')}</option>
          </select>
        </label>
      </div>

      {plan.warnings.map((w, i) => (
        <p key={i} className={`notice ${w.level === 'error' ? 'bad' : w.level === 'warn' ? 'warn' : ''}`}>
          <Icon name={w.level === 'info' ? 'info' : 'alert'} size={16} />{w.message}
        </p>
      ))}

      <ol className="timeline">
        {plan.steps.map((step) => {
          const done = now > step.at
          const isNext = next?.id === step.id
          return (
            <li key={step.id} className={[
              'step', done ? 'done' : '', isNext ? 'next' : '', step.isDeadline ? 'deadline' : '',
            ].join(' ').trim()}>
              <span className="step-time">{clock(step.at)}</span>
              <span className="step-dot"><Icon name={ICON[step.kind]} size={15} /></span>
              <span className="step-body">
                <strong>{step.title}</strong>
                {step.detail && <em>{step.detail}</em>}
                {/* The deadline is the one that costs the day if missed. */}
                {step.isDeadline && <em className="deadline-mark">{t('plan.deadline')}</em>}
              </span>
            </li>
          )
        })}
      </ol>

      <p className="muted small">
        {originOf(trip.route)} → {destinationOf(trip.route)} · {trip.vessel_name}
        {plan.doorToDoorMinutes ? ` · ${t('plan.doorToDoor')} ${Math.floor(plan.doorToDoorMinutes / 60)}h ${plan.doorToDoorMinutes % 60}m` : ''}
      </p>
    </div>
  )
}
